import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, ChevronRight, ChevronLeft, ArrowLeft, BookOpen, HelpCircle, ExternalLink,
  Rocket, CalendarDays, Globe, FileCheck, Users, ClipboardList, Receipt, Settings,
  AlertCircle, Mail, MessageSquare, Lightbulb, CheckCircle2
} from "lucide-react";
import {
  HELP_CATEGORIES, HELP_ARTICLES, HELP_SCREENSHOTS,
  getArticlesByCategory, searchArticles, getRelatedArticles, getArticleById, getCategoryById
} from "@/data/helpContent";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

// Icon mapping for categories
const CATEGORY_ICONS = {
  "getting-started": Rocket,
  "calendar": CalendarDays,
  "online-booking": Globe,
  "forms": FileCheck,
  "patients": Users,
  "notes": ClipboardList,
  "invoices": Receipt,
  "settings": Settings,
  "troubleshooting": AlertCircle,
};

/* ── Article View ── */
function ArticleView({ articleId, onBack }) {
  const article = getArticleById(articleId);
  const category = article ? getCategoryById(article.category) : null;
  const relatedArticles = article ? getRelatedArticles(articleId) : [];
  const screenshot = article?.screenshotId ? HELP_SCREENSHOTS[article.screenshotId] : null;

  if (!article) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-[#757675]/30 mx-auto mb-3" />
        <p className="text-sm text-[#757675]">Article not found</p>
        <button onClick={() => onBack()} className="text-sm text-[#205B5A] hover:underline mt-2">
          Back to Help Centre
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Back button */}
      <button
        onClick={() => onBack()}
        className="flex items-center gap-1.5 text-sm text-[#757675] hover:text-[#1A1B1A] mb-4"
        data-testid="article-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Help Centre
      </button>

      {/* Category badge */}
      {category && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-medium text-[#205B5A] bg-[#205B5A]/10 px-2.5 py-1 rounded-full">
            {category.name}
          </span>
        </div>
      )}

      {/* Article header */}
      <h1 className="text-2xl font-medium text-[#1A1B1A] mb-3" style={H}>
        {article.title}
      </h1>
      <p className="text-sm text-[#757675] mb-6 leading-relaxed">
        {article.summary}
      </p>

      {/* Main content card */}
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden mb-6">
        {/* Steps */}
        <div className="p-6">
          <h2 className="text-sm font-semibold text-[#1A1B1A] mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#205B5A]" />
            Steps
          </h2>
          <ol className="space-y-3">
            {article.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#205B5A]/10 text-[#205B5A] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm text-[#4A4B4A] leading-relaxed pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Screenshot with annotations (if available) */}
        {screenshot && (
          <div className="border-t border-[#1A1B1A]/[0.06] p-6 bg-[#FAF9F6]">
            <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">
              Screenshot
            </h3>
            <div className="relative bg-white rounded-xl border border-[#1A1B1A]/10 overflow-hidden">
              {screenshot.url ? (
                <img
                  src={screenshot.url}
                  alt={screenshot.alt}
                  className="w-full h-auto"
                />
              ) : (
                <div className="aspect-video bg-gradient-to-br from-[#FAF9F6] to-[#E8E7E3] flex items-center justify-center">
                  <p className="text-xs text-[#757675]">Screenshot placeholder - {screenshot.alt}</p>
                </div>
              )}
              {/* Annotations */}
              {screenshot.annotations?.map((ann) => (
                <div
                  key={ann.id}
                  className="absolute"
                  style={{ left: `${ann.x}%`, top: `${ann.y}%`, transform: "translate(-50%, -50%)" }}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-[#205B5A] text-white flex items-center justify-center text-[10px] font-bold shadow-lg">
                      {ann.id}
                    </span>
                    <span className="text-[10px] font-medium text-[#1A1B1A] bg-white/90 px-2 py-0.5 rounded shadow whitespace-nowrap">
                      {ann.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        {article.tips && article.tips.length > 0 && (
          <div className="border-t border-[#1A1B1A]/[0.06] p-6">
            <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5" />
              Tips
            </h3>
            <ul className="space-y-2">
              {article.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#4A4B4A]">
                  <span className="text-[#205B5A] mt-1">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">
            Related articles
          </h3>
          <div className="space-y-2">
            {relatedArticles.map((rel) => (
              <button
                key={rel.id}
                onClick={() => onBack(rel.id)}
                className="w-full text-left px-4 py-3 bg-white rounded-xl border border-[#1A1B1A]/[0.06] hover:border-[#205B5A]/30 transition-colors flex items-center justify-between group"
              >
                <span className="text-sm text-[#1A1B1A] group-hover:text-[#205B5A]">{rel.title}</span>
                <ChevronRight className="w-4 h-4 text-[#757675] group-hover:text-[#205B5A]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Still need help footer */}
      <div className="bg-[#FAF9F6] rounded-2xl border border-[#1A1B1A]/[0.06] p-6 text-center">
        <HelpCircle className="w-8 h-8 text-[#205B5A]/30 mx-auto mb-2" />
        <h4 className="text-sm font-medium text-[#1A1B1A] mb-1">Still need help?</h4>
        <p className="text-xs text-[#757675] mb-3">
          Contact our support team for personalized assistance.
        </p>
        <a
          href="mailto:support@lumeroflow.com"
          className="inline-flex items-center gap-1.5 text-sm text-[#205B5A] hover:text-[#174544]"
        >
          <Mail className="w-3.5 h-3.5" />
          support@lumeroflow.com
        </a>
      </div>
    </div>
  );
}

/* ── Category Card ── */
function CategoryCard({ category, onClick }) {
  const Icon = CATEGORY_ICONS[category.id] || BookOpen;
  const articleCount = getArticlesByCategory(category.id).length;

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5 text-left hover:border-[#205B5A]/30 hover:shadow-sm transition-all group w-full"
      data-testid={`category-${category.id}`}
    >
      <div className="w-10 h-10 rounded-xl bg-[#205B5A]/10 flex items-center justify-center mb-3 group-hover:bg-[#205B5A]/15 transition-colors">
        <Icon className="w-5 h-5 text-[#205B5A]" />
      </div>
      <h3 className="text-sm font-medium text-[#1A1B1A] mb-1 group-hover:text-[#205B5A] transition-colors">
        {category.name}
      </h3>
      <p className="text-xs text-[#757675] mb-2 line-clamp-2">{category.description}</p>
      <span className="text-[10px] text-[#757675]">{articleCount} article{articleCount !== 1 ? "s" : ""}</span>
    </button>
  );
}

/* ── Article List Item ── */
function ArticleListItem({ article, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-5 py-4 border-b border-[#1A1B1A]/[0.03] last:border-0 hover:bg-[#FAF9F6] transition-colors flex items-center justify-between group"
      data-testid={`article-${article.id}`}
    >
      <div className="min-w-0 flex-1 mr-3">
        <p className="text-sm font-medium text-[#1A1B1A] group-hover:text-[#205B5A] transition-colors truncate">
          {article.title}
        </p>
        <p className="text-xs text-[#757675] truncate">{article.summary}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[#757675] group-hover:text-[#205B5A] flex-shrink-0" />
    </button>
  );
}

/* ── Search Results ── */
function SearchResults({ query, results, onSelectArticle }) {
  if (results.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 text-center">
        <Search className="w-8 h-8 text-[#757675]/30 mx-auto mb-2" />
        <p className="text-sm text-[#757675]">No results found for &ldquo;{query}&rdquo;</p>
        <p className="text-xs text-[#757675] mt-1">Try different keywords or browse categories below.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
      <div className="px-5 py-3 border-b border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
        <p className="text-xs text-[#757675]">
          {results.length} result{results.length !== 1 ? "s" : ""} for &ldquo;{query}&rdquo;
        </p>
      </div>
      {results.map((article) => (
        <ArticleListItem key={article.id} article={article} onClick={() => onSelectArticle(article.id)} />
      ))}
    </div>
  );
}

/* ── Category View ── */
function CategoryView({ categoryId, onBack, onSelectArticle }) {
  const category = getCategoryById(categoryId);
  const articles = getArticlesByCategory(categoryId);
  const Icon = CATEGORY_ICONS[categoryId] || BookOpen;

  if (!category) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-[#757675]/30 mx-auto mb-3" />
        <p className="text-sm text-[#757675]">Category not found</p>
        <button onClick={() => onBack()} className="text-sm text-[#205B5A] hover:underline mt-2">
          Back to Help Centre
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Back button */}
      <button
        onClick={() => onBack()}
        className="flex items-center gap-1.5 text-sm text-[#757675] hover:text-[#1A1B1A] mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Help Centre
      </button>

      {/* Category header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#205B5A]/10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#205B5A]" />
        </div>
        <div>
          <h1 className="text-xl font-medium text-[#1A1B1A]" style={H}>{category.name}</h1>
          <p className="text-sm text-[#757675]">{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Articles list */}
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
        {articles.map((article) => (
          <ArticleListItem key={article.id} article={article} onClick={() => onSelectArticle(article.id)} />
        ))}
      </div>
    </div>
  );
}

/* ── Main Help Centre ── */
export default function SupportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  
  // Get view state from URL params
  const articleId = searchParams.get("article");
  const categoryId = searchParams.get("category");
  
  // Search results
  const searchResults = useMemo(() => {
    if (activeSearch.length < 2) return [];
    return searchArticles(activeSearch);
  }, [activeSearch]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
    // Clear URL params when searching
    setSearchParams({});
  };

  // Navigation handlers
  const goToArticle = (id) => {
    setSearchParams({ article: id });
    setActiveSearch("");
    setSearchQuery("");
  };

  const goToCategory = (id) => {
    setSearchParams({ category: id });
    setActiveSearch("");
    setSearchQuery("");
  };

  const goHome = (newArticleId = null) => {
    if (newArticleId) {
      setSearchParams({ article: newArticleId });
    } else {
      setSearchParams({});
    }
  };

  // Featured/popular articles for the home view
  const featuredArticles = useMemo(() => {
    const featured = [
      "set-availability",
      "enable-online-booking",
      "assign-forms",
      "complete-form-on-device",
      "create-treatment-note",
      "create-invoice",
    ];
    return featured
      .map(id => HELP_ARTICLES.find(a => a.id === id))
      .filter(Boolean)
      .slice(0, 6);
  }, []);

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="help-centre">
      {/* Article view */}
      {articleId && (
        <ArticleView articleId={articleId} onBack={goHome} />
      )}

      {/* Category view */}
      {categoryId && !articleId && (
        <CategoryView categoryId={categoryId} onBack={goHome} onSelectArticle={goToArticle} />
      )}

      {/* Home view */}
      {!articleId && !categoryId && (
        <>
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#205B5A]/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-7 h-7 text-[#205B5A]" />
            </div>
            <h1 className="text-2xl font-medium text-[#1A1B1A] mb-2" style={H}>
              LumeroFlow Help Centre
            </h1>
            <p className="text-sm text-[#757675] max-w-md mx-auto">
              Find guides, tutorials, and troubleshooting help for all LumeroFlow features.
            </p>
          </div>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#757675]" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search help articles..."
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm focus:outline-none focus:border-[#205B5A] focus:ring-1 focus:ring-[#205B5A]"
                data-testid="help-search"
              />
              {searchQuery && (
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#205B5A] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#174544]"
                >
                  Search
                </button>
              )}
            </div>
          </form>

          {/* Search results */}
          {activeSearch && (
            <div className="mb-8">
              <SearchResults
                query={activeSearch}
                results={searchResults}
                onSelectArticle={goToArticle}
              />
            </div>
          )}

          {/* Categories grid */}
          {!activeSearch && (
            <>
              <div className="mb-8">
                <h2 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-4">
                  Browse by category
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {HELP_CATEGORIES.map((cat) => (
                    <CategoryCard key={cat.id} category={cat} onClick={() => goToCategory(cat.id)} />
                  ))}
                </div>
              </div>

              {/* Popular articles */}
              <div className="mb-8">
                <h2 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-4">
                  Popular articles
                </h2>
                <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                  {featuredArticles.map((article) => (
                    <ArticleListItem key={article.id} article={article} onClick={() => goToArticle(article.id)} />
                  ))}
                </div>
              </div>

              {/* Contact support card */}
              <div className="bg-gradient-to-br from-[#205B5A]/5 to-[#205B5A]/10 rounded-2xl border border-[#205B5A]/10 p-6 flex flex-col sm:flex-row items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-[#205B5A]" />
                </div>
                <div className="text-center sm:text-left flex-1">
                  <h3 className="text-sm font-medium text-[#1A1B1A] mb-1">Need more help?</h3>
                  <p className="text-xs text-[#757675]">
                    Can&apos;t find what you&apos;re looking for? Our support team is here to help.
                  </p>
                </div>
                <a
                  href="mailto:support@lumeroflow.com"
                  className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5 flex-shrink-0"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Contact support
                </a>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
